/**
 * The frame-rate journal (M30 #11): a 30-second summary of the level's frames — median and low frame rate, the
 * JavaScript and render-submission time, draw calls — kept in a small ring for Settings → Copy diagnostics, with the
 * page's hide/show moments in between. Kevin reported "not 60 fps all the time in battles or on the completion
 * screen; backgrounding then foregrounding the app restores 60 for a while": the journal shows, after the fact,
 * whether the frame rate fell with time since the page was last shown (heat, or something accumulating), with the
 * level's progress (stage, units), or at a fixed point — without the perf overlay having to be on.
 *
 * Pure bookkeeping, fed the frame's numbers by the level loop; no clock of its own, so it is unit-tested in Node.
 */

export interface FrameWindow {
  /** Wall-clock time the window closed (ms since the epoch). */
  readonly atMs: number;
  /** What was on screen ("L6 wave 4", "L6 end screen"). */
  readonly label: string;
  readonly frames: number;
  /** Frame rate from the median interval, and from the 95th-percentile interval (the slow frames). */
  readonly fps: number;
  readonly fpsLow: number;
  /** Median JavaScript before the render, and median render submission, per frame (ms). */
  readonly jsMs: number;
  readonly renderMs: number;
  /** Median draw calls of the frames that rendered. */
  readonly draws: number;
  /** Seconds since the page was last shown (or loaded) when the window closed. */
  readonly sinceShownS: number;
  /**
   * M34: sim ticks per frame (mean) and the ticks the clock's pacer held back in the window (level/tickPacer.ts). A
   * window at ~30 fps with two ticks a frame and nothing held back is the trap the pacer exists to break.
   */
  readonly ticks?: number;
  readonly held?: number;
}

export type JournalLine = { readonly kind: "frames"; readonly w: FrameWindow } | { readonly kind: "page"; readonly atMs: number; readonly text: string };

export class FrameJournal {
  /** Length of one window of frames (ms of frame time). */
  static readonly WindowMs = 30_000;
  /** A gap between frames this long is a stall or the page away: the window restarts instead of counting it. */
  static readonly GapMs = 1000;
  /** Lines kept: an hour of play at one line a 30 s window, with room for the page's comings and goings. */
  static readonly Capacity = 140;

  private readonly lines: JournalLine[] = [];
  private intervals: number[] = [];
  private js: number[] = [];
  private render: number[] = [];
  private draws: number[] = [];
  private ticks = 0;
  private heldAtStart = -1;
  private heldNow = 0;
  private windowStart = -1;
  private last = -1;
  private shownAtMs: number;
  private hiddenAtMs = -1;

  constructor(private readonly wallClock: () => number = Date.now) { this.shownAtMs = wallClock(); }

  /**
   * One level frame at `now` (the rAF timestamp, ms) that spent `jsMs` before its render and `renderMs` submitting
   * it (0 when it did not render), with `draws` draw calls (0 when it did not render). Returns the window it closed.
   * `ticks`: sim ticks the frame ran; `held`: the pacer's running count of ticks held back (both optional).
   */
  frame(now: number, jsMs: number, renderMs: number, draws: number, label: string, ticks = 0, held = 0): FrameWindow | null {
    this.heldNow = held;
    if (this.last < 0 || now - this.last > FrameJournal.GapMs || now < this.last) { this.restart(now); return null; }
    this.ticks += ticks;
    this.intervals.push(now - this.last);
    this.last = now;
    this.js.push(jsMs);
    if (draws > 0) { this.render.push(renderMs); this.draws.push(draws); }
    if (now - this.windowStart < FrameJournal.WindowMs) return null;
    const med = (xs: number[]) => (xs.length === 0 ? 0 : quantile(xs, 0.5));
    const at = this.wallClock();
    const w: FrameWindow = {
      atMs: at, label, frames: this.intervals.length,
      fps: round1(1000 / Math.max(1, med(this.intervals))), fpsLow: round1(1000 / Math.max(1, quantile(this.intervals, 0.95))),
      jsMs: round1(med(this.js)), renderMs: round1(med(this.render)), draws: Math.round(med(this.draws)),
      sinceShownS: Math.round((at - this.shownAtMs) / 1000),
      ticks: round1(this.ticks / Math.max(1, this.intervals.length)), held: held >= this.heldAtStart ? held - this.heldAtStart : held,
    };
    this.push({ kind: "frames", w });
    this.restart(now);
    return w;
  }

  /** The page went out of sight (true) or came back (false). */
  visibility(hidden: boolean): void {
    const at = this.wallClock();
    if (hidden) { this.hiddenAtMs = at; this.push({ kind: "page", atMs: at, text: "page hidden" }); return; }
    const away = this.hiddenAtMs >= 0 ? Math.round((at - this.hiddenAtMs) / 1000) : -1;
    this.hiddenAtMs = -1;
    this.shownAtMs = at;
    this.last = -1;                                      // the next frame starts a fresh window
    this.push({ kind: "page", atMs: at, text: away >= 0 ? `page shown after ${away} s away` : "page shown" });
  }

  /** A moment worth seeing beside the frame rate (a reload's reason, a lost context). */
  note(text: string): void { this.push({ kind: "page", atMs: this.wallClock(), text }); }

  entries(): readonly JournalLine[] { return this.lines; }

  private restart(now: number): void {
    this.intervals = []; this.js = []; this.render = []; this.draws = [];
    this.ticks = 0; this.heldAtStart = this.heldNow;
    this.windowStart = now; this.last = now;
  }

  private push(l: JournalLine): void {
    this.lines.push(l);
    if (this.lines.length > FrameJournal.Capacity) this.lines.splice(0, this.lines.length - FrameJournal.Capacity);
  }
}

/** One journal line as the diagnostics text shows it. */
export function journalLineText(l: JournalLine): string {
  const t = new Date(l.kind === "frames" ? l.w.atMs : l.atMs).toISOString().slice(11, 19);
  if (l.kind === "page") return `  ${t} ${l.text}`;
  const w = l.w;
  const pace = w.ticks !== undefined ? ` · ${w.ticks} ticks/frame · ${w.held ?? 0} held` : "";
  return `  ${t} ${w.label}: ${w.fps} fps (slow ${w.fpsLow}) · js ${w.jsMs} ms · render ${w.renderMs} ms · ${w.draws} draws${pace} · ${w.sinceShownS} s since shown`;
}

function quantile(xs: readonly number[], q: number): number {
  const s = xs.slice().sort((a, b) => a - b);
  return s[Math.min(s.length - 1, Math.floor(s.length * q))];
}
const round1 = (x: number) => Math.round(x * 10) / 10;

/** The one journal of the app (the level loop feeds it; Settings → Copy diagnostics prints it). */
export const frameJournal = new FrameJournal();

let installed = false;
/** Follows the page's visibility into the journal. Idempotent; no-op outside a browser. */
export function installFrameJournal(): void {
  if (installed || typeof document === "undefined") return;
  installed = true;
  document.addEventListener("visibilitychange", () => frameJournal.visibility(document.hidden));
}
